import { useGetReservationByIdQuery } from '@massilia/queries'
import { createFileRoute, Link } from '@tanstack/react-router'
import { ArrowLeft, Check, Mail, Phone, User } from 'lucide-react'

import { AGENCY_EMAIL, AGENCY_PHONE } from '@/lib/constants/agency'
import { formatDateShort } from '@/utils/format-date'

import { LoadingSkeleton } from '../reservation/-components/loading-skeleton'
import { ReservationError } from '../reservation/-components/reservation-error'

export const Route = createFileRoute('/_blue_header/reservation-confirmation/$reservationId')({
  component: ReservationConfirmationPage,
  errorComponent: ReservationError,
})

function OptionItem({ label, selected }: { label: string; selected: boolean }) {
  return (
    <div className="text-gray-light-700 flex items-center gap-2 text-sm">
      {selected ? (
        <Check className="h-4 w-4 text-green-600" />
      ) : (
        <span className="h-4 w-4 text-center text-gray-400">-</span>
      )}
      <span className={selected ? '' : 'text-gray-400'}>{label}</span>
    </div>
  )
}

function ReservationConfirmationPage() {
  const { reservationId } = Route.useParams()

  const {
    data: reservation,
    error,
    isLoading,
  } = useGetReservationByIdQuery({
    id: reservationId,
  })

  if (isLoading) {
    return <LoadingSkeleton />
  }

  if (error || !reservation) {
    return <ReservationError error={error} />
  }

  return (
    <>
      {/* Success header */}
      <div className="flex flex-col items-center gap-4">
        <div className="flex h-16 w-16 items-center justify-center rounded-full bg-green-100">
          <Check className="h-8 w-8 text-green-600" />
        </div>
        <div className="text-center">
          <h1 className="text-gray-light-900 text-2xl font-semibold">
            Demande de réservation reçue
          </h1>
          <p className="text-gray-light-600 mt-1 text-sm">
            Référence : {reservation.estimateResponseId}
          </p>
        </div>
      </div>

      {/* Content sections */}
      <div className="mx-auto flex w-full max-w-2xl flex-col gap-4">
        {/* Contact details */}
        <div className="border-gray-light-200 rounded-lg border p-4">
          <h2 className="text-gray-light-900 mb-3 text-sm font-semibold">Coordonnées de contact</h2>
          <div className="space-y-2">
            <div className="text-gray-light-700 flex items-center gap-2 text-sm">
              <Mail className="h-4 w-4" />
              <span>{reservation.client.email ?? 'Non renseigné'}</span>
            </div>
            {reservation.otherEmailAddress && (
              <div className="text-gray-light-700 flex items-center gap-2 text-sm">
                <Mail className="h-4 w-4" />
                <span>{reservation.otherEmailAddress} (secondaire)</span>
              </div>
            )}
            <div className="text-gray-light-700 flex items-center gap-2 text-sm">
              <Phone className="h-4 w-4" />
              <span>{reservation.client.phoneNumber}</span>
            </div>
            {reservation.otherContactPhone && (
              <div className="text-gray-light-700 flex items-center gap-2 text-sm">
                <Phone className="h-4 w-4" />
                <span>{reservation.otherContactPhone} (secondaire)</span>
              </div>
            )}
          </div>
        </div>

        {/* Passengers */}
        <div className="border-gray-light-200 rounded-lg border p-4">
          <h2 className="text-gray-light-900 mb-3 text-sm font-semibold">
            Passagers ({reservation.passengers.length})
          </h2>
          <div className="space-y-2">
            {reservation.passengers.map((passenger) => (
              <div
                key={passenger.id}
                className="text-gray-light-700 flex items-center gap-2 text-sm"
              >
                <User className="h-4 w-4" />
                <span>
                  {passenger.firstName} {passenger.lastName} -{' '}
                  {formatDateShort(passenger.birthDate)}
                </span>
              </div>
            ))}
          </div>
        </div>

        {/* Selected options */}
        <div className="border-gray-light-200 rounded-lg border p-4">
          <h2 className="text-gray-light-900 mb-3 text-sm font-semibold">Options sélectionnées</h2>
          <div className="space-y-2">
            <OptionItem label="Assurance annulation" selected={reservation.wantsInsurance} />
            <OptionItem label="Prêt de voiture" selected={reservation.wantsCarLoan} />
            <OptionItem label="Facture demandée" selected={reservation.wantsInvoice} />
          </div>
        </div>

        {/* Next steps */}
        <div className="bg-gray-light-50 rounded-lg p-4">
          <h2 className="text-gray-light-900 mb-2 text-sm font-semibold">Prochaines étapes</h2>
          <p className="text-gray-light-600 text-sm">
            Notre équipe va traiter votre demande et vous recontacter prochainement pour finaliser
            votre réservation.
          </p>
        </div>

        {/* Agency contact */}
        <div className="border-gray-light-200 rounded-lg border p-4">
          <h2 className="text-gray-light-900 mb-3 text-sm font-semibold">Besoin d&apos;aide ?</h2>
          <div className="space-y-2">
            <div className="text-gray-light-700 flex items-center gap-2 text-sm">
              <Phone className="h-4 w-4" />
              <span>{AGENCY_PHONE}</span>
            </div>
            <div className="text-gray-light-700 flex items-center gap-2 text-sm">
              <Mail className="h-4 w-4" />
              <span>{AGENCY_EMAIL}</span>
            </div>
          </div>
        </div>

        {/* Back to home button */}
        <Link
          to="/"
          className="inline-flex items-center justify-center gap-2 rounded-lg bg-[#1D95AD] px-6 py-3 font-medium text-white transition-colors hover:bg-[#1a8599]"
        >
          <ArrowLeft className="h-4 w-4" />
          Retour à l&apos;accueil
        </Link>
      </div>
    </>
  )
}
